import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { ABO_TARGET, EXPORT_TARGET, OFFENE_ZAHLUNG_LESEZUGRIFF_TEXT, ZAHLUNG_OFFEN_TEXT, checkoutErrorText, openBillingPortal, portalErrorText, startAboCheckout, vertragsendeBannerText } from "@/lib/accountPolicy";
import {
  LayoutDashboard,
  Users,
  BookOpen,
  FolderArchive,
  Settings,
  LogOut,
  AlertCircle,
  KeyRound,
  Clock,
  ShieldAlert,
  Section,
  IdCard,
  Hand,
  FlaskConical,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { VorgaengeProvider } from "@/hooks/useVorgaenge";
import { useOwnerSession } from "@/hooks/useOwnerSession";
import VorgangsTray from "@/components/VorgangsTray";
import SetupWizard from "@/components/SetupWizard";
import DemoUpgrade from "@/components/DemoUpgrade";
import ChrisHelper from "@/components/chris/ChrisHelper";
import VertragsendeSperrseite from "@/components/VertragsendeSperrseite";
// MOBIL-NAV-02, Betreiberfreigabe 29.10.2026 — Menü-Knopf der Handy-Kopfleiste (unter 768 px)
import UnternehmerHandyNav from "@/components/UnternehmerHandyNav";

// UI-REDESIGN Ergänzung (Aug 2026, betreibergenehmigt):
// Reihenfolge + 4 Umbenennungen.
// ICON-UMSTELLUNG (Sep 2026, betreibergenehmigt): Alle 9 Menüpunkte rendern
// das lucide-Icon aus `icon` als Filled-Badge (40x40, bg-slate-900, weißes
// 20px-Icon — Referenzstil aus Einstellungen.jsx; aktive Zeile: bg-white/10).
// Die früheren `iconSrc`-PNGs (/public/nav-icons/) wurden nach Abnahme des
// Badge-Looks am 19.09.2026 vollständig entfernt (Betreiberauftrag).
// Slot-Reihenfolge: Unterweisungsübersicht -> Gefährdungsbeurteilungen ->
// BG Prüfmappe (verschoben direkt nach Gefährdungsbeurteilungen) ->
// Aushängepflichtige Gesetze -> Führerschein Dokumentation -> Mitarbeiter ->
// Einstellungen. `sublabel` rendert eine kleine Klammer-Zusatzzeile
// (kein Link, rein informativ; nur für Führerschein Dokumentation
// "Fahrerqualifizierungsnachweis" gesetzt).
const NAV_ITEMS = [
  { to: "/unternehmer", label: "Unterweisungsübersicht", icon: LayoutDashboard, end: true, testId: "nav-dashboard" },
  { to: "/unternehmer/unterweisungen", label: "Gefährdungsbeurteilungen", icon: BookOpen, testId: "nav-unterweisungen" },
  { to: "/unternehmer/pruefmappe", label: "BG Prüfmappe", icon: FolderArchive, testId: "nav-pruefmappe" },
  // A1-B (Feb 2026): Aushangpflicht als eigenständiger Sidebar-Eintrag —
  // ergänzt (nicht ersetzt) die bestehende Dashboard-Kachel.
  // Icon seit 19.09.2026 (Betreiber-Wahl): lucide `Section` = §-Paragraphenzeichen.
  // MODUL-REITER-02, Betreiberfreigabe 29.10.2026: Schreibweise „Aushangpflichtige“.
  { to: "/unternehmer/aushang", label: "Aushangpflichtige Gesetze", icon: Section, testId: "nav-aushang" },
  // CLARTEXT · Fuehrerscheinkontrolle · Block F (Feb 2026): eigener
  // Sidebar-Eintrag mit Badge-Zaehler (Frage 8a + Frage 9-Antwort).
  // Block H (Feb 2026 · §11.i/E9): featureModule-Gate — Eintrag nur sichtbar,
  // wenn das Modul fuer den Mandanten aktiviert ist (fail-closed).
  // MODUL-REITER-02, Betreiberfreigabe 29.10.2026: immer sichtbar (kein featureModule); ohne Modul zeigt die Seite den Hinweis „nicht gebucht“.
  { to: "/unternehmer/fuehrerschein", label: "Führerschein Dokumentation", sublabel: "Fahrerqualifizierungsnachweis", icon: IdCard, testId: "nav-fuehrerschein", badgeKey: "fsk_open" },
  // Handschutzplan-Modul · Baustein 5 (Aug 2026, Betreiberfreigabe 2a):
  // eigener, hazmat_addon-gegateter Eintrag direkt nach Führerschein.
  // Sidebar-Anzeige seit 09/2026 (Betreiberauftrag): "Hautschutzplan" mit
  // Zusatzzeile "& Handschutzplan" — REINE Anzeige-Änderung, Route/Feature/
  // interne Benennung bleiben "handschutzplan".
  { to: "/unternehmer/handschutzplan", label: "Hautschutzplan", sublabel: "& Handschutzplan", icon: Hand, testId: "nav-handschutzplan", featureModule: "hazmat" },
  // Gefahrstoffverzeichnis (Aug 2026, § 6 GefStoffV): Owner-Leseansicht,
  // hazmat-gegated wie der Handschutzplan-Eintrag, direkt dahinter.
  { to: "/unternehmer/gefahrstoffverzeichnis", label: "Gefahrstoffverzeichnis", icon: FlaskConical, testId: "nav-gefahrstoffverzeichnis", featureModule: "hazmat" },
  { to: "/unternehmer/mitarbeiter", label: "Mitarbeiter", icon: Users, testId: "nav-mitarbeiter" },
  { to: "/unternehmer/einstellungen", label: "Einstellungen", icon: Settings, testId: "nav-einstellungen" },
];

// A1-B (Feb 2026): NAV_ITEMS wird als reiner Regressionstest-Vertrag
// exportiert. Kein produktiver Konsument, ausschließlich zur Absicherung
// der Sidebar-Konstellation gegen versehentliche Änderungen.
export const __NAV_ITEMS_FOR_TESTS__ = NAV_ITEMS;

export default function UnternehmerLayout() {
  const navigate = useNavigate();
  const { session: ownerSession, logout: ownerLogout } = useOwnerSession();
  const [company, setCompany] = useState(null);
  // Block F · Frage 9: eigener Badge-Zaehler direkt am Sidebar-Eintrag,
  // KEINE Aggregation im plattformweiten Handlungsbedarf.
  const [fskOpenCount, setFskOpenCount] = useState(0);
  // Block H (Feb 2026 · E9): Modul-Feature-Status fuer das Sidebar-Gate.
  // Fail-closed: solange nicht positiv bestaetigt, bleibt der Eintrag verborgen.
  // Der Zahlstatus wird NICHT hier, sondern als Banner auf der Detailseite gezeigt.
  const [moduleFeatures, setModuleFeatures] = useState({});

  // STUFE 6 (Juli 2026): Im anonymen Landingpage-/Preview-Flow (Mandant
  // "public-preview") werden interne Verwaltungsfunktionen nur AUSGEBLENDET.
  // Eingeloggte Unternehmer sehen die App unverändert.
  const istOeffentlicheVorschau = ownerSession?.tenant_id === "public-preview";

  useEffect(() => {
    api.getCompany().then(setCompany).catch(() => {});
  }, []);

  // Block H (Feb 2026 · E9): Modul-Aktivierung einmalig laden (fail-closed:
  // Fehler → Eintrag bleibt ausgeblendet).
  useEffect(() => {
    api
      .getModuleAddonStatus("fuehrerschein")
      .then((s) =>
        setModuleFeatures((prev) => ({ ...prev, fuehrerschein: !!s?.module_active }))
      )
      .catch(() => {});
  }, []);

  // Handschutzplan-Modul · Baustein 5 (Aug 2026): Gefahrstoff-Modul-Gate
  // für den Sidebar-Eintrag (fail-closed, identisches Muster zu Block H).
  useEffect(() => {
    api
      .getModuleAddonStatus("hazmat_addon")
      .then((s) =>
        setModuleFeatures((prev) => ({ ...prev, hazmat: !!s?.module_active }))
      )
      .catch(() => {});
  }, []);

  // Block F · Badge-Refresh: einmalig beim Mount + alle 60s. Kein
  // aggressiver Poll — nur ein Zaehler. Block H: Poll nur bei aktivem Modul.
  useEffect(() => {
    if (!moduleFeatures.fuehrerschein) return undefined;
    let cancelled = false;
    const load = () =>
      api
        .countOpenFskRecords()
        .then((r) => {
          if (!cancelled) setFskOpenCount(Number(r?.open || 0));
        })
        .catch(() => {});
    load();
    const timer = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [moduleFeatures.fuehrerschein]);

  // Block H (Feb 2026 · E9): Sidebar-Gate — Eintraege mit featureModule nur
  // anzeigen, wenn das Modul fuer den Mandanten aktiv ist (fail-closed).
  const visibleNavItems = NAV_ITEMS.filter(
    (item) => !item.featureModule || moduleFeatures[item.featureModule]
  );

  const needsSetup = company && !company.is_configured;
  const needsWizard = company && !company.setup_completed;
  const mustChangePin = !!ownerSession?.must_change_pin;

  // Chris C2A (Juni 2026) – mandantenweites Mount-Gate VOR <ChrisHelper />.
  // Effektive Semantik (BE-C2A-001..003):
  //  - company noch nicht geladen (null) → NICHT mounten (fail-closed, kein Flicker)
  //  - company geladen, Feld fehlt/null/undefined → mounten (Default EIN)
  //  - chris_enabled === false → NICHT mounten (AUS gewinnt, auch bei ?chris=poc)
  // C1 bleibt dadurch byte-identisch: das Gate liegt ausschließlich hier davor.
  const chrisEnabled = !!company && (company.chris_enabled ?? true);

  // G4 – Trial-Transparenz (reine Anzeige aus bereits geladenem company-State).
  // FIX-01b-2 (Betreiberfreigabe 12.10.): Lesezugriff-Banner aus dem zentralen
  // `access_state` (read_only) statt aus account_status; Testphasen-Banner (demo mit
  // Resttagen) nur, wenn der Zugang NICHT bereits durch Zahlung/Lizenz gewährt ist.
  // Bezahlte Kunden sehen damit nie den Lesezugriff-Banner. Texte unverändert.
  const trialInfo = (() => {
    if (!company) return null;
    const access = company.access_state;
    const status = company.account_status;
    if (access && access.read_only === true) {
      if (access.reason === "read_only_expired") return { variant: "expired" };
      if (access.reason === "read_only_license_inaktiv") return { variant: "license_inaktiv" };
      // ZAHL-02d (Betreiberfreigabe 14.10., Soll 5): Lesephase nach Vertragsende → eigener Banner.
      if (access.reason === "read_only_vertragsende") return { variant: "vertragsende", vertragsende: access.vertragsende || null };
      return null; // suspended / invalid / gesperrt_vertragsende (eigene Seite): kein Banner
    }
    // ZAHL-02b (Betreiberfreigabe 13.10., Soll 6): Abo past_due → „Zahlung fehlgeschlagen" bei vollem Zugriff.
    if (company.payment_state && company.payment_state.zahlung_offen === true) return { variant: "zahlung_offen" };
    if (access && access.license_status) return null; // Vollzugriff durch Lizenz
    if (status === "demo" && company.trial_ends_at) {
      const end = new Date(company.trial_ends_at);
      if (Number.isNaN(end.getTime())) return null;
      const days = Math.max(0, Math.ceil((end.getTime() - Date.now()) / 86400000));
      const dateStr = end.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      return { variant: "demo", days, dateStr, fristVergangen: end.getTime() < Date.now() };
    }
    if (!access && status === "expired") return { variant: "expired" };
    return null;
  })();

  // BUCH-01 (Betreiberfreigabe 12.10.): „Abo abschließen" nur für Kunden ohne bezahltes
  // Abo (Testphase / Testphase abgelaufen); nicht bei Zahlung in Prüfung, Lizenz,
  // suspended, Lizenz ungültig oder Ausnahmen. Ausnahme-Mandanten (Master, Sandbox,
  // DEMO, Pilot) erkennt man daran, dass die Frist vergangen ist, der Zugang aber
  // dennoch nicht lesend ist (E10) – dort kein Abo-Hinweis.
  const showAboCta =
    !!trialInfo &&
    (trialInfo.variant === "demo" || trialInfo.variant === "expired") &&
    !trialInfo.fristVergangen &&
    !company?.access_state?.payment_pending &&
    !company?.access_state?.license_status;

  // ZAHL-02b (Betreiberfreigabe 13.10., Z2/Soll 6): „Zahlungsdaten ändern" öffnet das
  // Stripe-Kundenportal (POST /api/billing/portal-session, ziel zahlungsdaten) in derselben
  // Registerkarte. Fehler (kein Stripe-Kunde / Portal nicht erreichbar) → Text aus der Route.
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState("");
  const openZahlungsdaten = async () => {
    if (portalBusy) return;
    setPortalBusy(true);
    setPortalError("");
    try {
      await openBillingPortal(api, "zahlungsdaten");
    } catch (e) {
      setPortalError(portalErrorText(e));
    } finally {
      setPortalBusy(false);
    }
  };
  const zahlungsdatenButton = (testId) => (
    <div className="mt-2 flex flex-wrap items-center gap-3">
      <button
        type="button"
        onClick={openZahlungsdaten}
        disabled={portalBusy}
        className="inline-flex items-center rounded-md border border-amber-400 bg-white px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-60 transition-colors"
        data-testid={testId}
      >
        Zahlungsdaten ändern
      </button>
      {portalError && (
        <span className="text-sm text-red-700" role="alert" data-testid="owner-portal-error">
          {portalError}
        </span>
      )}
    </div>
  );

  const handleSetupCompleted = (res) => {
    if (res?.company) {
      setCompany(res.company);
    } else {
      api.getCompany().then(setCompany);
    }
  };

  const handleLogout = async () => {
    await ownerLogout();
    navigate("/", { replace: true });
  };

  // ZAHL-02d (Betreiberfreigabe 14.10., D6/Soll 5): „Abo neu abschließen" im Lesephase-Banner → bestehender
  // Abo-Checkout (POST /api/billing/checkout) → Weiterleitung auf Stripe; Fehler als Text im Banner.
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const neuAbschliessen = async () => {
    if (checkoutBusy) return;
    setCheckoutBusy(true);
    setCheckoutError("");
    try {
      const res = await startAboCheckout(api);
      if (!res || !res.url) {
        setCheckoutError(checkoutErrorText(null));
        setCheckoutBusy(false);
      }
    } catch (e) {
      setCheckoutError(checkoutErrorText(e));
      setCheckoutBusy(false);
    }
  };

  // ZAHL-02d (Betreiberfreigabe 14.10., D5): nach Ende der Lesephase nur die Hinweisseite – keine Navigation,
  // keine Inhalte. Der Abo-Checkout (D6) und das Abmelden bleiben erreichbar.
  if (company?.access_state?.reason === "gesperrt_vertragsende") {
    return (
      <VertragsendeSperrseite
        vertragsende={company.access_state.vertragsende || null}
        onLogout={handleLogout}
        onReload={() => api.getCompany().then(setCompany).catch(() => {})}
      />
    );
  }

  return (
    <VorgaengeProvider inWizard={!!needsWizard}>
      <div className="min-h-screen bg-slate-50 flex">
        {/* Sidebar */}
        <aside
          className="hidden lg:flex w-80 shrink-0 bg-white border-r border-slate-200 flex-col"
          data-testid="unternehmer-sidebar"
        >
          <div className="px-6 py-6 border-b border-slate-100">
            <img
              src="/brand/clartext-logo-full-light.svg"
              alt="CLartext"
              className="h-11 w-auto max-w-full object-contain mb-4"
              data-testid="app-logo-sidebar"
            />
            <div className="text-xs uppercase tracking-widest text-slate-400 font-medium">
              Unternehmen
            </div>
            <div
              className="font-heading text-lg font-bold text-slate-900 mt-1 break-words"
              data-testid="sidebar-company-name"
            >
              {company?.name || "Lade…"}
            </div>
            <VorgangsTray variant="desktop" />
          </div>

          {!istOeffentlicheVorschau && (
            <nav className="flex-1 p-3 space-y-1">
              {visibleNavItems.map((item) => {
                const badge =
                  item.badgeKey === "fsk_open" && fskOpenCount > 0
                    ? fskOpenCount
                    : null;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    data-testid={item.testId}
                    className={({ isActive }) =>
                      cn(
                        "group flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                        isActive
                          ? "bg-slate-900 text-white"
                          : "text-slate-700 hover:bg-slate-100"
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {/* Filled-Badge (Referenzstil Einstellungen.jsx); auf der
                            aktiven dunklen Zeile invertiert zu bg-white/10.
                            Inaktiv: dezente Aufhellung beim Zeilen-Hover. */}
                        <span
                          className={cn(
                            "w-10 h-10 rounded-md text-white flex items-center justify-center shrink-0 transition-colors duration-200",
                            isActive
                              ? "bg-white/10"
                              : "bg-slate-900 group-hover:bg-slate-700"
                          )}
                          data-testid={`${item.testId}-icon-badge`}
                        >
                          <item.icon className="w-5 h-5" />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block leading-tight">{item.label}</span>
                          {item.sublabel && (
                            <span
                              className="block text-[10px] font-normal opacity-70 leading-tight mt-0.5"
                              data-testid={`${item.testId}-sublabel`}
                            >
                              {item.sublabel}
                            </span>
                          )}
                        </span>
                        {badge !== null && (
                          <span
                            data-testid="nav-fuehrerschein-badge"
                            className="inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-semibold min-w-[1.25rem] h-5 px-1.5"
                          >
                            {badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </nav>
          )}

          {!istOeffentlicheVorschau && (
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="m-3 flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Abmelden
            </button>
          )}
        </aside>

        {/* Mobile top bar */}
        <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-slate-200 z-40">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2 min-w-0">
              <img
                src="/brand/clartext-icon.svg"
                alt="CLartext"
                className="h-10 w-10 shrink-0"
                data-testid="app-logo-mobile"
              />
              <div className="font-heading font-bold text-slate-900 truncate pr-2">
                {company?.name || "Lade…"}
              </div>
            </div>
            {/* MOBIL-NAV-02, Betreiberfreigabe 29.10.2026 — Menü-Knopf (nur unter 768 px, md:hidden) neben „Abmelden“ */}
            {!istOeffentlicheVorschau && (
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={handleLogout}
                  className="text-sm text-slate-600"
                  data-testid="logout-button-mobile"
                >
                  Abmelden
                </button>
                <UnternehmerHandyNav items={visibleNavItems} />
              </div>
            )}
          </div>
          <div className="px-3 pb-2">
            <VorgangsTray variant="mobile" />
          </div>
          {/* MOBIL-NAV-02, Betreiberfreigabe 29.10.2026 — Reiterstreifen nur unter 768 px ausgeblendet (hidden md:flex) */}
          {!istOeffentlicheVorschau && (
            <nav className="hidden md:flex overflow-x-auto px-2 pb-2 gap-1">
              {visibleNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap",
                      isActive
                        ? "bg-slate-900 text-white"
                        : "text-slate-600 bg-slate-100"
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {/* Kompakte Badge-Variante (24px) desselben Filled-Stils. */}
                      <span
                        className={cn(
                          "w-6 h-6 rounded-md text-white flex items-center justify-center shrink-0",
                          isActive ? "bg-white/10" : "bg-slate-900"
                        )}
                        data-testid={`${item.testId}-icon-badge-mobile`}
                      >
                        <item.icon className="w-3.5 h-3.5" />
                      </span>
                      {item.label}
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
          )}
        </div>

        {/* MOBIL-NAV-02, Betreiberfreigabe 29.10.2026 — unter 768 px Kopfleiste ohne Streifen 65 px → pt-[68px]; ab 768 px pt-28 wie bisher */}
        <main className="flex-1 min-w-0 lg:py-10 lg:px-10 pt-[68px] md:pt-28 pb-10 px-4">
          {trialInfo?.variant === "demo" && (
            <div
              data-testid="owner-trial-banner"
              data-trial-variant="demo"
              className="mb-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm"
            >
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                <div className="flex-1 text-amber-900">
                  <span className="font-medium">Testphase aktiv:</span>{" "}
                  noch {trialInfo.days} {trialInfo.days === 1 ? "Tag" : "Tage"} · gültig bis{" "}
                  {trialInfo.dateStr} · keine automatische Verlängerung
                  {/* BUCH-01 (Betreiberfreigabe 12.10.): Weg zum Abo-Abschluss (Tarifkarte). */}
                  {showAboCta && (
                    <>
                      {" · "}
                      <Link
                        to={ABO_TARGET}
                        className="font-medium underline"
                        data-testid="owner-trial-banner-abo-cta"
                      >
                        Abo abschließen
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          {trialInfo?.variant === "expired" && (
            <div
              data-testid="owner-trial-banner"
              data-trial-variant="expired"
              className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm"
            >
              <div className="flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
                <div className="flex-1 text-red-900">
                  <span className="font-medium">Testphase abgelaufen:</span>{" "}
                  Ihr Konto befindet sich im Lesezugriff. Bestehende Inhalte und Nachweise
                  bleiben verfügbar.
                  {showAboCta && (
                    <>
                      {" "}
                      <Link
                        to={ABO_TARGET}
                        className="font-medium underline"
                        data-testid="owner-trial-banner-abo-cta"
                      >
                        Abo abschließen
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          {trialInfo?.variant === "vertragsende" && (
            <div
              data-testid="owner-trial-banner"
              data-trial-variant="vertragsende"
              className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm"
            >
              <div className="flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
                <div className="flex-1 text-red-900">
                  {/* ZAHL-02d (Betreiberfreigabe 14.10., Soll 5): Lesephase nach Vertragsende – Text wortgleich. */}
                  <span className="font-medium">Vertrag beendet:</span> {vertragsendeBannerText(trialInfo.vertragsende)}
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <Link
                      to={EXPORT_TARGET}
                      className="inline-flex items-center rounded-md border border-red-400 bg-white px-3 py-1.5 text-sm font-medium text-red-900 hover:bg-red-100 transition-colors"
                      data-testid="owner-vertragsende-export-btn"
                    >
                      Nachweise exportieren
                    </Link>
                    <button
                      type="button"
                      onClick={neuAbschliessen}
                      disabled={checkoutBusy}
                      className="inline-flex items-center rounded-md bg-red-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-60 transition-colors"
                      data-testid="owner-vertragsende-abo-btn"
                    >
                      Abo neu abschließen
                    </button>
                    {checkoutError && (
                      <span className="text-sm text-red-700" role="alert" data-testid="owner-vertragsende-checkout-error">
                        {checkoutError}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {trialInfo?.variant === "license_inaktiv" && (
            <div
              data-testid="owner-trial-banner"
              data-trial-variant="license_inaktiv"
              className="mb-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm"
            >
              <div className="flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                <div className="flex-1 text-amber-900">
                  {/* ZAHL-02b (Betreiberfreigabe 13.10., Soll 6): neuer Wortlaut + „Zahlungsdaten ändern". */}
                  <span className="font-medium">Offene Zahlung:</span> {OFFENE_ZAHLUNG_LESEZUGRIFF_TEXT}
                  {zahlungsdatenButton("owner-trial-banner-zahlungsdaten")}
                </div>
              </div>
            </div>
          )}
          {trialInfo?.variant === "zahlung_offen" && (
            <div
              data-testid="owner-trial-banner"
              data-trial-variant="zahlung_offen"
              className="mb-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm"
            >
              <div className="flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                <div className="flex-1 text-amber-900">
                  {/* ZAHL-02b (Betreiberfreigabe 13.10., Soll 6): Mahnlauf läuft, voller Zugriff. */}
                  <span className="font-medium">Zahlung fehlgeschlagen:</span> {ZAHLUNG_OFFEN_TEXT}
                  {zahlungsdatenButton("owner-trial-banner-zahlungsdaten")}
                </div>
              </div>
            </div>
          )}
          {mustChangePin && (
            <Link
              to="/unternehmer/einstellungen"
              data-testid="owner-pin-banner"
              className="block mb-6 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm hover:bg-rose-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <KeyRound className="w-5 h-5 text-rose-700 shrink-0" />
                <div className="flex-1">
                  <div className="font-medium text-rose-900">
                    Master-PIN ändern
                  </div>
                  <div className="text-rose-800 text-xs mt-0.5">
                    Sie verwenden noch Ihre temporäre PIN. Bitte vergeben Sie jetzt
                    einen eigenen 6-stelligen PIN in den Einstellungen →
                  </div>
                </div>
              </div>
            </Link>
          )}
          {needsSetup && (
            <Link
              to="/unternehmer/einstellungen"
              data-testid="setup-banner"
              className="block mb-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm hover:bg-amber-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-700 shrink-0" />
                <div className="flex-1">
                  <div className="font-medium text-amber-900">
                    Firmendaten ergänzen
                  </div>
                  <div className="text-amber-800 text-xs mt-0.5">
                    Sobald die verantwortliche Person und Adresse hinterlegt sind, erscheinen
                    sie automatisch auf jedem Nachweis. Jetzt ergänzen →
                  </div>
                </div>
              </div>
            </Link>
          )}
          <DemoUpgrade
            ownerSession={ownerSession}
            company={company}
            onDone={() => api.getCompany().then(setCompany).catch(() => {})}
          />
          <Outlet context={{ company, onCompanyUpdated: setCompany }} />
        </main>
      </div>
      {needsWizard && (
        <SetupWizard onCompleted={handleSetupCompleted} />
      )}
      {/* CHRIS · C2A — mandantenweites EIN/AUS-Gate VOR dem C1-Overlay. AUS
          verhindert das Mounten vollständig (keine Assets/Listener/Figur),
          gewinnt auch gegen ?chris=poc. C1 selbst bleibt unverändert. */}
      {chrisEnabled && <ChrisHelper />}
    </VorgaengeProvider>
  );
}
